<template>
    <div>
        <slot name="before-table">
            <oc-plugin-slot name="before-table" ref="beforeTable" />
        </slot>
        <table :id="id" v-class="$_tableClasses">
            <thead v-if="displayHeader">
                <slot name="before-header" :columns="getColumns()">
                    <oc-plugin-slot name="before-header" v-bind="{columns: getColumns()}" ref="beforeHeader" />
                </slot>

                <slot name="header-row" :columns="getColumns()">
                    <tr>
                        <slot name="before-columns">
                            <oc-plugin-slot name="before-columns" ref="beforeColumns" />
                        </slot>

                        <slot name="columns" :columns="getColumns()">
                            <slot v-for="(col, key) in getColumns()" v-if="isVisible(col)" :name="`column-${col.field}`" :col="col">
                                <oc-table-column v-bind="getColumnAttrs(col)">
                                    <component
                                        v-if="col.header.component && ! col.type"
                                        :is="col.header.component"
                                        v-bind="getEntityOptions('header', col)"
                                        v-on="getEntityEvents('header', col)"
                                    />
                                    <oc-plugin-slot
                                        v-else
                                        name="column-default"
                                        v-bind="getEntityOptions('header', col)"
                                        v-on="getEntityEvents('header', col)"
                                        ref="columnDefault"
                                    />
                                </oc-table-column>
                            </slot>
                        </slot>

                        <slot name="after-columns">
                            <oc-plugin-slot name="after-columns" ref="afterColumns" />
                        </slot>
                    </tr>
                </slot>

                <slot name="after-header" :columns="getColumns()">
                    <oc-plugin-slot name="after-header" v-bind="{columns: getColumns()}" ref="afterHeader" />
                </slot>
            </thead>
            <tbody>
                <template v-if="loading">
                    <slot name="loading-row">
                        <tr>
                            <oc-table-cell field="-loading-" :content="textLoading" :colspan="columns.length" class="loading-row">
                                <p>{{textLoading}}</p>
                            </oc-table-cell>
                        </tr>
                    </slot>
                </template>
                <template v-else>
                    <template v-if="$_data.length">
                        <template v-for="(item, index) in getDataWithRowAttrs()">
                            <slot
                                name="body-row"
                                v-bind="item.rowAttrs.attrs"
                                :style="item.rowAttrs.style"
                                :row="item.row"
                                :index="index"
                                :uniqueIndex="getRowUniqueIndex(item.row, index)"
                                :columns="getColumns()"
                                :onRowClick="$_onRowClick()"
                            >
                                <oc-table-row
                                    v-bind="item.rowAttrs.attrs"
                                    :style="item.rowAttrs.style"
                                    :row="item.row"
                                    :index="index"
                                    :columns="getColumns()"
                                    :onRowClick="$_onRowClick()"
                                />
                            </slot>
                        </template>
                    </template>
                    <template v-else>
                        <slot name="no-records-row">
                            <tr>
                                <oc-table-cell field="-no-records-" :content="textNoRecordsFound" :colspan="columns.length" class="no-records-found">
                                    <oc-no-results-found :text="textNoRecordsFound">
                                        <oc-plugin-slot name="no-records-row" :content="textNoRecordsFound" />
                                    </oc-no-results-found>
                                </oc-table-cell>
                            </tr>
                        </slot>
                    </template>
                </template>
            </tbody>

            <tfoot v-if="displayFooter">
                <slot name="footer-row">
                    <oc-plugin-slot name="footer-row" ref="footerRow" />
                </slot>
            </tfoot>
        </table>
        <slot name="after-table">
            <oc-plugin-slot name="after-table" ref="afterTable" />
        </slot>
    </div>
</template>

<script>
import {filterSlotVNodes} from '@/helpers/Utils'
import {cloneObject, Inflector} from '@utils'

export default {
    name: 'oc-table',
    // We provide it in the main component in order to fix provide issue in combination with nested elements.
    // For example <advanced><Table> - we prevent <Table> for using loaded plugins of the parent advanced Table.
    provide() {
        return {
            $plugins: false
        }
    },
    props: {
        data: {
            default: () => [],
            validator(val) {
                for ( let row of val ) {
                    if( typeOf(row) !== 'object' ) {
                        throw new Error('[Table] Invalid "data" param. It needs to be an array with objects.')
                    }
                }

                return true
            }
        },
        columns: {
            type: Array,
            default: () => []
        },
        actions: {
            type: Array,
            default: () => []
        },
        onRowClick: {
            type: [Function, Boolean],
            default: null
        },
        loading: {
            type: Boolean,
            default: false
        },
        id: {
            type: [String, Number]
        },
        tableClasses: {
            default: () => ['table', 'table-striped', 'table-hover']
        },
        displayHeader: {
            type: Boolean,
            default: true
        },
        displayFooter: {
            type: Boolean,
            default: true
        },
        textNoRecordsFound: {
            type: String,
            default: 'No records found!'
        },
        textLoading: {
            type: String,
            default: 'Loading ...'
        },
        rowOptions: {
            type: [Object, Function],
            default: () => ({})
        }
    },
    data() {
        return {
            slotColumns: []
        }
    },
    created() {
        // if columns are passed through slot register it in the columns property
        let columnsVNodes = filterSlotVNodes(this, 'columns', 'table-column')

        if (columnsVNodes.length) {
            for (let column of columnsVNodes) {
                this.slotColumns.push(column.componentOptions.propsData)
            }
        }

        // we call this just trigger columns parsing logic
        // this is needed for plugins to have formatted columns
        this.getColumns()

        /**
         * [
         *     (row, index) => ({}),
         *     (row, index) => ({})
         * ]
         *
         * we want it not reactive
         */
        this.internalRowOptions = []

        this.internalTableClasses = []

        this.defaultOnRowClick = false
    },
    methods: {
        // used so it can be overwritten via plugin mixins
        getColumns() {
            if( 0 === this.$_columns.length ) {
                throw new Error('[Table] Please provide at least one column via "columns" prop or the corespond slot.')
            }

            return this.$_columns
        },
        $_onRowClick() {
            return this.onRowClick !== null ? this.onRowClick : this.defaultOnRowClick
        },
        getRowUniqueIndex(row, index) {
            return index
        },
        getColumnAttrs(col) {
            let attrs = Object.assign({}, col)

            if( col.type === undefined && col.header.component === undefined ) {
                attrs = Object.assign(attrs, this.getEntityOptions('header', col).options)
            }

            return attrs
        },
        getDataWithRowAttrs() {
            return this.$_data.map( (row, index) => {
                return {
                    row,
                    rowAttrs: this.getRowAttrs(row, index)
                }
            })
        },
        getRowAttrs(row, index) {
            let attrs = {
                row,
                index
            }

            let internalRowOptions = this.getRowOptionsInternal(row, index)

            let rowOptions = typeOf(this.rowOptions) === 'function'
                ? this.rowOptions(row, index)
                : this.rowOptions

            // handle style
            let style = []

            if(internalRowOptions.style !== undefined ) {
                style.push(internalRowOptions.style)
            }

            if(rowOptions.style !== undefined ) {
                style.push(rowOptions.style)
            }
            //

            return {
                attrs: Object.assign(
                    attrs,
                    internalRowOptions,
                    rowOptions
                ),
                style
            }
        },
        getRowOptionsInternal(row, index) {
            if( this.internalRowOptions.length === 0 ) {
                return {}
            }

            let options = {}
            this.internalRowOptions.forEach( func => {
                Object.assign(options, func(row, index))
            })

            return options
        },
        getEntityOptions(type, ...args) {
            let [column, row, index] = args

            let options = {}
            if (typeof column[type].options === "function") {
                options = column[type].options.apply(null, args)
            } else if ( column[type].options !== undefined ) {
                options = column[type].options
            }

            let result = {col: column, options}

            if( row !== undefined ) result.row = row
            if( index !== undefined ) result.index = index

            return result
        },
        getEntityEvents(type, ...args) {
            let [column, row, index] = args

            if (typeof column[type].events === "function") {
                return column[type].events.apply(null, args)
            }

            return column[type].events
        },
        isVisible(column) {
            if( typeof column.visible === 'function' ) {
                return column.visible()
            }

            return column.visible === undefined || column.visible
        },
        formatColumn(column) {
            // support for column description: ['col1', 'col2']
            if( typeOf(column) === 'string' ) {
                column = {
                    field: column
                }
            }

            if( column.title === undefined && column.field !== undefined ) {
                column.title = Inflector.camel2words(column.field, true)
            }

            if( column.header === undefined ) column.header = {}
            if( column.header.options === undefined ) column.header.options = {}

            if( column.content === undefined ) column.content = {}
            if( column.content.options === undefined ) column.content.options = {}

            return column
        }
    },
    computed: {
        $_tableClasses() {
            return [this.tableClasses, this.internalTableClasses]
        },
        $_columns() {
            // holds column field names for further optimizations
            this.$_columnsRegister = []

            // we do not clone the this.columns array so to be able to re-render the component (@see dtable plugin)
            let columns = this.slotColumns.length ? this.slotColumns : this.columns

            for (let index in columns) {
                columns[index] = this.formatColumn(columns[index])

                if( columns[index].field !== undefined ) {
                    this.$_columnsRegister.push(columns[index].field)
                }
            }

            return columns
        },
        $_data() {
            return Array.isArray(this.data) ? this.data : []
        }
    }
}
</script>
